import { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import useGenerateAvatar from '../../hooks/useGenerateAvatar'
import { color, space } from '../../styles'
import SVG from '../../assets/svg'

interface AvatarSelectorProps {
   roleID: string
   displayName: string
   onClickUpload: () => any
   croppedImage: {
      value: string | null
      imageUUID: string
   }
   onChange: (value: string) => any
}

// ------------------------
// AvatarSelector component
// ------------------------
const AvatarSelector: React.FC<AvatarSelectorProps> = (props) => {
   const { onClickUpload, croppedImage, onChange } = props
   const [selectedGeneratedAvatar, setSelectedGeneratedAvatar] = useState<number | null>(null)
   const [image, setImage] = useState<string>('')

   const { generatedAvatars, generateNewAvatars } = useGenerateAvatar(props)

   // Set image as avatar after cropping
   useEffect(() => {
      if (croppedImage.value !== null) {
         setImage(croppedImage.value)
         setSelectedGeneratedAvatar(null)
      }
   }, [croppedImage.imageUUID])

   // Set image as avatar after selecting generated avatar
   useEffect(() => {
      selectedGeneratedAvatar !== null && setImage(generatedAvatars[selectedGeneratedAvatar])
   }, [selectedGeneratedAvatar])

   const handleGenerateNewAvatars = () => {
      generateNewAvatars()
      setSelectedGeneratedAvatar(null)
   }

   // Set the avatar image value
   useEffect(() => {
      onChange(image)
   }, [image])

   return (
      <Holder data-testid="avatar-selector">
         <span className="image-upload " onClick={onClickUpload} data-testid="upload-button">
            <div className="border" />
            <SVG.form.uploadToCloud />
         </span>
         {generatedAvatars.map((image, index) => (
            <div
               className={'avatar-item ' + (selectedGeneratedAvatar === index ? 'selected' : '')}
               key={index}
               onClick={() => setSelectedGeneratedAvatar(index)}
            >
               <div className="avatar-container">
                  <img src={image} alt="" />
               </div>
            </div>
         ))}
         <div className="avatar-item reset-button" onClick={handleGenerateNewAvatars}>
            <div className="avatar-container">
               <SVG.form.reset />
            </div>
         </div>
      </Holder>
   )
}

const Holder = styled.div`
   position: relative;
   margin-block: -0.9em;
   display: grid;
   grid-template-columns: repeat(8, 1fr);
   grid-template-rows: 1fr;
   gap: 3em;
   background-color: #f8f8f8;
   width: 35.5em;
   padding: 1.8em ${space.l};
   padding-top: 2.5em;
   border: 1px solid #d8d8d8;
   border-radius: 0.6em;
   overflow-x: auto;
   font-size: 0.8em;
   z-index: 1;

   /* Change scroll bar style */
   &::-webkit-scrollbar {
      height: 0.6em;
      width: 0.6em;
   }
   &::-webkit-scrollbar-track {
      background: #d8d8d8;
   }
   &::-webkit-scrollbar-thumb {
      background: #a8a8a8;
      border-radius: 0.6em;
      &:hover {
         background: #7a7a7a;
      }
   }

   .avatar-item {
      font-size: 0.93em;
      cursor: pointer;
      border: 1px solid #b3b3b3;
      border-radius: 50%;
      box-sizing: border-box;
      opacity: 0.7;
      height: 5.4em;
      aspect-ratio: 1/1;
      display: flex;
      justify-content: center;
      align-items: center;
      /* disable css selection */
      -webkit-touch-callout: none; /* iOS Safari */
      -webkit-user-select: none; /* Safari */
      -khtml-user-select: none; /* Konqueror HTML */
      -moz-user-select: none; /* Old versions of Firefox */
      -ms-user-select: none; /* Internet Explorer/Edge */
      user-select: none; /* Non-prefixed version, currently
                           supported by Chrome, Edge, Opera and Firefox */

      .avatar-container {
         border-radius: 50%;
         overflow: hidden;
         width: 4.2em;
         aspect-ratio: 1/1;
         display: flex;
         justify-content: center;
         align-items: center;

         img {
            width: 107%;
            display: block;
            pointer-events: none;
         }
      }

      &.selected {
         border-color: ${color.violet};
         border-width: 3px;
         opacity: 1;
         .avatar-container {
            border: none;
            width: 5em;
         }
      }

      &:hover {
         border: 3px solid ${color.green};
         opacity: 0.85;
      }

      /* Reset button */
      &.reset-button {
         .avatar-container {
            border: none !important;
         }
         svg {
            width: 1.8em;
            fill: #646464;
            transition: all 0.4s;
         }
         &:hover {
            fill: ${color.green};
            svg {
               fill: #3f3f3f;
               transform: scale(1.2);
            }
         }
      }
   }

   .image-upload {
      display: flex;
      justify-content: center;
      align-items: center;
      position: relative;
      border-radius: 50%;
      overflow: hidden;
      height: 5em;
      width: 5em;
      box-sizing: border-box;
      isolation: isolate;
      cursor: pointer;
      svg {
         position: absolute;
         top: 29%;
         left: 26%;
         width: 2.4em;
         fill: #646464;
         transition: transform 0.2s ease-in-out;
         pointer-events: none;
      }
      .border {
         position: absolute;
         z-index: -1;
         top: 0;
         left: 0;
         border: 3px dashed #646464;
         border-radius: 50%;
         background-color: #d8d8d8;
         width: 90%;
         height: 90%;
         pointer-events: none;
      }
      &:hover {
         .border {
            animation: rotate 10s linear infinite;
         }
         svg {
            transform: scale(1.1);
            fill: #424242;
         }
      }

      /* rotate animation keyframe */
      @keyframes rotate {
         0% {
            transform: rotate(0deg);
         }
         100% {
            transform: rotate(360deg);
         }
      }
   }
`
export default AvatarSelector
